import { Navigate, Route, Routes } from '@do/seashell/navigation';
import { AnalyticsSnippet } from '@segment/analytics-next';
import { ErrorBoundary, withProfiler } from '@sentry/react';
import React, { ReactElement, useEffect, useState } from 'react';
import { InviteErrorPage } from './components/InviteErrorPage';
import Login from './components/login/Login';
import PasswordForgot from './components/PasswordForgot';
import PasswordReset from './components/PasswordReset';
import InterstitialRegistration from './components/Registration';
import SessionExpired from './components/SessionExpired';
import { SiftScriptWrapper } from './components/SiftScript';
import { REGISTER_SHOW_FORM_STEP } from './constants';
import initializeSegment from './utils/segment-script';
import { ErrorState } from '@do/walrus';
import { RegistrationTemplateProvider } from './components/RegistrationTemplateProvider';
import { SSOErrorPage } from './components/SSOErrorPage';
import { SSOVerifyPage } from './components/SSOVerifyPage';

declare global {
  interface Window {
    analytics: AnalyticsSnippet;
  }
}

const SiftRouteWrapper: React.FC = ({ children }) => {
  return (
    <>
      <SiftScriptWrapper />
      {children}
    </>
  );
};

export function App(): ReactElement {
  const [hasInitializedAnalytics, setHasInitializedAnalytics] = useState(false);

  // load the Segment script only once
  useEffect(() => {
    const isInitialized = window.analytics?.initialized;

    if (!hasInitializedAnalytics && !isInitialized) {
      initializeSegment();

      window.analytics.ready(() => {
        setHasInitializedAnalytics(true);
      });
    }
  }, [hasInitializedAnalytics]);

  return (
    <ErrorBoundary fallback={<ErrorState />}>
      <RegistrationTemplateProvider>
        <Routes>
          <Route path="/registrations/new" element={<InterstitialRegistration />} />
          <Route path="/registration-next" element={<Navigate to="/registrations/new" replace />} />
          <Route path="/registrations/email" element={<InterstitialRegistration step={REGISTER_SHOW_FORM_STEP} />} />
          <Route path="/login" element={<Login />} />
          <Route
            path="/forgot_password/new"
            element={
              <SiftRouteWrapper>
                <PasswordForgot />
              </SiftRouteWrapper>
            }
          />
          <Route
            path="/password_resets/:token"
            element={
              <SiftRouteWrapper>
                <PasswordReset />
              </SiftRouteWrapper>
            }
          />
          <Route
            path="/timeout"
            element={
              <SiftRouteWrapper>
                <SessionExpired />
              </SiftRouteWrapper>
            }
          />
          <Route
            path="/invites/error"
            element={
              <SiftRouteWrapper>
                <InviteErrorPage />
              </SiftRouteWrapper>
            }
          />
          <Route
            path="/sso/error"
            element={
              <SiftRouteWrapper>
                <SSOErrorPage />
              </SiftRouteWrapper>
            }
          />
          <Route
            path="/sso/verify"
            element={
              <SiftRouteWrapper>
                <SSOVerifyPage />
              </SiftRouteWrapper>
            }
          />
        </Routes>
      </RegistrationTemplateProvider>
    </ErrorBoundary>
  );
}

export default withProfiler(App);
